import React from 'react';
import { useEffect, useState, useMemo, useRef } from "react";
import 'src/assets/styles/react-table.scss';
import 'src/components/CustomAppLayout.scss';
import ServiceCollection from "src/services/ServiceCollection";
import { TPReportsSummaryRequest } from "src/models/reports/TPReportsSummary";
import { ReportGenerationSummaryRequest } from "src/models/reports/ReportGenerationSummary";
import { Popover, SelectProps } from '@amzn/awsui-components-react/polaris';
import TPPieChart from '../../shared/TPPieChart';
import {
    Box,
    Button,
    ColumnLayout,
    Container,
    SpaceBetween,
    Spinner,
    Grid,
    Header,
    SegmentedControl,
} from '@amzn/awsui-components-react';
import ReportMetricsDashboardFilterSelector from './ReportMetricsDashboardFilterSelector';
import MARReportSummarySection from './MARReportSummarySection';
import LEFReportSummarySection from './LEFReportSummarySection';
import CWBReportSummarySection from './CWBReportSummarySection';
import { convertCamelCaseToTitleCase, generatePieChartData } from './util';
import { REPORT_GENERATION_SUMMARY_CUTOFF_PERIOD } from './DashboardConstants';

const REPORT_TYPE_ALL = 'ALL';
const REPORT_TYPE_MAR = 'MAR';
const REPORT_TYPE_LEF = 'LEF';
const REPORT_TYPE_CWB = 'CWB';
const REPORT_TYPE_VAR = 'VAR';

const REPORT_TYPE_OPTIONS = [
    { text: 'All Reports', id: REPORT_TYPE_ALL },
    { text: 'MAR', id: REPORT_TYPE_MAR },
    { text: 'LEF', id: REPORT_TYPE_LEF },
    { text: 'CWB', id: REPORT_TYPE_CWB },
    { text: 'VAR', id: REPORT_TYPE_VAR },
] as const;

type ComponentProps = {
    services: ServiceCollection;
    metricsEnabled?: boolean;
}

export default function ReportMetricsDashboard(props: ComponentProps) {

    const { services, metricsEnabled = false } = props;
    const [tpVarianceDetails, setTpVarianceDetails] = useState<Map<string, number>>();
    const [reportingCLIExecutionStatusDetails, setReportingCLIExecutionStatusDetails] = useState<Map<string, number>>();
    const [mecCliExecutionStatusDetails, setMecCliExecutionStatusDetails] = useState<Map<string, number>>();
    const [totalCli, setTotalCli] = useState<number>(0);
    const [totalICRSRecords, setTotalICRSRecords] = useState<number>(0);
    const [totalTPRecords, setTotalTPRecords] = useState<number>(0);
    const [rootCauseCoverage, setRootCauseCoverage] = useState<number>(0);
    const cachedLefSummary = useRef<any>(null);
    const cachedCwbSummary = useRef<any>(null);
    const isFilterLoading = useRef(false);
    const [selectedPeriodNameOption, setSelectedPeriodNameOption] = React.useState(null as unknown as SelectProps.Option | null);
    const [selectedAccountingDayOption, setSelectedAccountingDayOption] = React.useState(null as unknown as SelectProps.Option | null);
    const [tpTaxOwner, setTpTaxOwner] = React.useState('');
    const [selectedReportType, setSelectedReportType] = React.useState(REPORT_TYPE_ALL);
    const [fetchedPeriod, setFetchedPeriod] = React.useState<string | null>(null);
    const [fetchedAccountingDay, setFetchedAccountingDay] = React.useState<string | null>(null);
    const [tpReportsSummaryRequest, setTpReportsSummaryRequest] = React.useState(null as unknown as TPReportsSummaryRequest);
    const [reportGenSummaryRequest, setReportGenSummaryRequest] = React.useState(null as unknown as ReportGenerationSummaryRequest);
    const [fetchTPReportSummaryResponse, fetchTpReportsSummaryLoading, tpReportsSummaryFetchError] = services.reportsService.fetchCompleteTPReportsSummary(tpReportsSummaryRequest);
    const [reportGenSummaryResponse, reportGenSummaryLoading, reportGenSummaryError] = services.reportsService.fetchReportGenerationSummary(reportGenSummaryRequest);

    const isNewApiPeriod = (period: string): boolean => {
        const monthOrder = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
        const parse = (p: string) => {
            const [mon, yr] = p.split('-');
            return parseInt(yr) * 12 + monthOrder.indexOf(mon);
        };
        return parse(period) >= parse(REPORT_GENERATION_SUMMARY_CUTOFF_PERIOD);
    };

    useEffect(() => {
        if (!tpReportsSummaryFetchError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(tpReportsSummaryFetchError);
    }, [tpReportsSummaryFetchError]);

    useEffect(() => {
        if (!reportGenSummaryError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(reportGenSummaryError);
    }, [reportGenSummaryError]);

    useEffect(() => {
        if (!fetchTPReportSummaryResponse) {
            return;
        }
        setTpVarianceDetails(fetchTPReportSummaryResponse.varianceAnalysis);
        setReportingCLIExecutionStatusDetails(fetchTPReportSummaryResponse.reportingServiceStatus);
        setMecCliExecutionStatusDetails(fetchTPReportSummaryResponse.atpCliStatus);
        setTotalCli(fetchTPReportSummaryResponse.atpExecutionDetailsByCount.totalNumberOfCLIs);
        setTotalICRSRecords(fetchTPReportSummaryResponse.atpExecutionDetailsByCount.totalNumberOfICRSEntries);
        setTotalTPRecords(fetchTPReportSummaryResponse.atpExecutionDetailsByCount.totalNumberOfEntriesExecutedByATP);
        setRootCauseCoverage(fetchTPReportSummaryResponse.rootCauseCoverage);
    }, [fetchTPReportSummaryResponse]);

    // Populate legacy metrics from new API response (periods >= cutoff)
    useEffect(() => {
        if (!reportGenSummaryResponse) {
            return;
        }
        isFilterLoading.current = false;
        // Cache LEF/CWB so they don't blank during MAR filter operations
        if (reportGenSummaryResponse.lefSummary !== undefined) {
            cachedLefSummary.current = reportGenSummaryResponse.lefSummary;
        }
        if (reportGenSummaryResponse.cwbSummary !== undefined) {
            cachedCwbSummary.current = reportGenSummaryResponse.cwbSummary;
        }
        if (reportGenSummaryResponse.reportingServiceStatus) {
            setReportingCLIExecutionStatusDetails(reportGenSummaryResponse.reportingServiceStatus as any);
        }
        if (reportGenSummaryResponse.varianceAnalysis) {
            setTpVarianceDetails(reportGenSummaryResponse.varianceAnalysis as any);
        }
        if (reportGenSummaryResponse.atpCliStatus) {
            setMecCliExecutionStatusDetails(reportGenSummaryResponse.atpCliStatus as any);
        }
        if (reportGenSummaryResponse.atpExecutionDetailsByCount) {
            setTotalCli(reportGenSummaryResponse.atpExecutionDetailsByCount.totalNumberOfCLIs);
            setTotalICRSRecords(reportGenSummaryResponse.atpExecutionDetailsByCount.totalNumberOfICRSEntries);
            setTotalTPRecords(reportGenSummaryResponse.atpExecutionDetailsByCount.totalNumberOfEntriesExecutedByATP);
        }
        if (reportGenSummaryResponse.rootCauseCoverage != null) {
            setRootCauseCoverage(reportGenSummaryResponse.rootCauseCoverage);
        }
    }, [reportGenSummaryResponse]);

    const fetchTPTaxReportMetricsData = () => {
        if (!!selectedAccountingDayOption && !!selectedPeriodNameOption) {
            const period = selectedPeriodNameOption?.value as string;
            const accountingDay = selectedAccountingDayOption?.value as string;
            // Clear legacy state to show empty/loading state
            setReportingCLIExecutionStatusDetails(undefined);
            setTpVarianceDetails(undefined);
            setMecCliExecutionStatusDetails(undefined);
            setTotalCli(0);
            setTotalICRSRecords(0);
            setTotalTPRecords(0);
            setRootCauseCoverage(0);
            cachedLefSummary.current = null;
            cachedCwbSummary.current = null;
            isFilterLoading.current = false;
            setFetchedPeriod(period);
            setFetchedAccountingDay(accountingDay);
            if (metricsEnabled && isNewApiPeriod(period)) {
                setReportGenSummaryRequest({ period, accountingDay, tpTaxOwner: tpTaxOwner || undefined });
            } else {
                setSelectedReportType(REPORT_TYPE_ALL);
                setTpReportsSummaryRequest({ period, accountingDay });
            }
        }
    }

    const reportingServiceStatusData = useMemo(() => generatePieChartData(reportingCLIExecutionStatusDetails, totalCli), [reportingCLIExecutionStatusDetails, totalCli]);
    const varianceAnalysisData = useMemo(() => generatePieChartData(tpVarianceDetails, totalCli), [tpVarianceDetails, totalCli]);
    const atpCliStatusData = useMemo(() => generatePieChartData(mecCliExecutionStatusDetails, totalCli), [mecCliExecutionStatusDetails, totalCli]);

    const shouldShowReport = (reportId: string) => selectedReportType === REPORT_TYPE_ALL || selectedReportType === reportId;
    const useNewApi = metricsEnabled && fetchedPeriod ? isNewApiPeriod(fetchedPeriod) : false;

    const cachedTpTaxOwnerOptionsRef = useRef<string[]>([]);

    const tpTaxOwnerOptions = useMemo(() => {
        if (tpTaxOwner) {
            return cachedTpTaxOwnerOptionsRef.current;
        }
        const owners = reportGenSummaryResponse?.marSummary?.failedCLIs
            ?.map((cli) => cli.tpTaxOwner)
            .filter((owner): owner is string => !!owner) || [];
        const uniqueOwners = [...new Set(owners)];
        cachedTpTaxOwnerOptionsRef.current = uniqueOwners;
        return uniqueOwners;
    }, [reportGenSummaryResponse, tpTaxOwner]);


    return (
        <Container header={<Header variant="h2"> Transfer Pricing Reporting Dashboard</Header>}>
            <SpaceBetween direction="vertical" size="m">
                <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                    <ReportMetricsDashboardFilterSelector
                        selectedAccountingPeriod={selectedPeriodNameOption}
                        selectedAccountingDay={selectedAccountingDayOption}
                        services={services}
                        onAccountingPeriodSelected={(periodOption) => setSelectedPeriodNameOption({
                            label: periodOption?.label,
                            value: periodOption?.value
                        })}
                        onAccountingDaySelected={(accountingDayOption) => setSelectedAccountingDayOption({
                            label: accountingDayOption?.label,
                            value: accountingDayOption?.value
                        })}
                    />
                    <Box float="left" padding={{ top: "xl" }}>
                        <SpaceBetween direction="horizontal" size="l">
                            <Button
                                onClick={fetchTPTaxReportMetricsData}
                                disabled={(!selectedAccountingDayOption || !selectedPeriodNameOption)}
                                loading={reportGenSummaryLoading}
                                variant="primary"
                            >
                                View Report
                            </Button>
                        </SpaceBetween>
                    </Box>
                </Grid>
                {/* Report type tabs (only for periods >= cutoff with feature flag enabled) */}
                {metricsEnabled && (!fetchedPeriod || useNewApi) && (
                <SegmentedControl
                    selectedId={selectedReportType}
                    onChange={({ detail }) => setSelectedReportType(detail.selectedId)}
                    options={[...REPORT_TYPE_OPTIONS]}
                />
                )}
                {/* New API sections (shown on initial load or periods >= cutoff, with feature flag enabled) */}
                {metricsEnabled && (!fetchedPeriod || useNewApi) && (
                    reportGenSummaryLoading && !!fetchedPeriod && !isFilterLoading.current ? (
                        <Box textAlign="center" padding={{ top: "xxl", bottom: "xxl" }}>
                            <Spinner size="large" />
                            <Box variant="p" color="inherit" padding={{ top: "s" }}>Loading report data...</Box>
                        </Box>
                    ) : (
                    <SpaceBetween size="s">
                        {shouldShowReport(REPORT_TYPE_MAR) && (
                            <MARReportSummarySection
                                summary={reportGenSummaryResponse?.marSummary}
                                loading={reportGenSummaryLoading && isFilterLoading.current}
                                tpTaxOwner={tpTaxOwner}
                                tpTaxOwnerOptions={tpTaxOwnerOptions}
                                onTpTaxOwnerChanged={setTpTaxOwner}
                                onApplyFilter={() => {
                                    if (fetchedPeriod && fetchedAccountingDay) {
                                        isFilterLoading.current = true;
                                        setReportGenSummaryRequest({
                                            period: fetchedPeriod,
                                            accountingDay: fetchedAccountingDay,
                                            tpTaxOwner: tpTaxOwner || undefined
                                        });
                                    }
                                }}
                                onClearFilter={() => {
                                    setTpTaxOwner('');
                                    if (fetchedPeriod && fetchedAccountingDay) {
                                        isFilterLoading.current = true;
                                        setReportGenSummaryRequest({
                                            period: fetchedPeriod,
                                            accountingDay: fetchedAccountingDay
                                        });
                                    }
                                }}
                            />
                        )}
                        {shouldShowReport(REPORT_TYPE_LEF) && (
                            <LEFReportSummarySection summary={reportGenSummaryResponse?.lefSummary ?? cachedLefSummary.current} />
                        )}
                        {shouldShowReport(REPORT_TYPE_CWB) && (
                            <CWBReportSummarySection summary={reportGenSummaryResponse?.cwbSummary ?? cachedCwbSummary.current} />
                        )}
                    </SpaceBetween>
                    )
                )}
                {/* Legacy metrics sections */}
                {(!useNewApi && fetchTpReportsSummaryLoading && !!fetchedPeriod) ? (
                    <Box textAlign="center" padding={{ top: "xxl", bottom: "xxl" }}>
                        <Spinner size="large" />
                        <Box variant="p" color="inherit" padding={{ top: "s" }}>Loading report data...</Box>
                    </Box>
                ) : !(useNewApi && reportGenSummaryLoading && !!fetchedPeriod && !isFilterLoading.current) && (
                    <SpaceBetween size="s">
                {/* Reporting Service Status - only for periods < cutoff (MAR covers this for new periods) */}
                {(fetchedPeriod && !useNewApi) && (
                <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
                    <Container header={
                        <Header variant="h2"
                            description="Status summary for all CLIs, excluding ICRS, ingested by the reporting service during the selected period.">
                            Reporting Service Status
                        </Header>}>
                        {reportingCLIExecutionStatusDetails ? (
                            <ColumnLayout columns={4} variant="text-grid">
                                {Object.entries(reportingCLIExecutionStatusDetails).map(([label, value], index) => (
                                    <div key={index}>
                                        <Box variant="awsui-key-label">{convertCamelCaseToTitleCase(label)}</Box>
                                        <Box variant='h1'>{value}</Box>
                                    </div>
                                ))}
                                <div>
                                    <Box variant="awsui-key-label">Total CLIs Processed</Box>
                                    <Box variant='h1'>{totalCli}</Box>
                                </div>
                            </ColumnLayout>
                        ) : (
                            <Box textAlign="center" color="inherit">
                                <b>No data available</b>
                                <Box variant="p" color="inherit">
                                    No reporting status details available to display
                                </Box>
                            </Box>
                        )}
                    </Container>
                    <TPPieChart
                        data={reportingServiceStatusData}
                        totalUnits={totalCli}
                        ariaDescription="Donut chart showing ATP reporting dashboard data."
                        ariaLabel="ATP reporting Donut chart"
                        emptyDataDescription='No report status data available to display on the chart'
                        noMatchDataDescription='No report status data available for the selected filter criteria'
                    />
                </Grid>
                )}
                {shouldShowReport(REPORT_TYPE_VAR) && (
                <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
                    <Container header={
                        <Header variant="h2" description="Summary of variances identified between targeted and actual results for CLIs during the selected period.">
                            Variance Analysis
                        </Header>}>
                        {tpVarianceDetails ? (
                            <ColumnLayout columns={4} variant="text-grid">
                                {Object.entries(tpVarianceDetails).map(([label, value], index) => (
                                    <div key={index}>
                                        <Box variant="awsui-key-label">{convertCamelCaseToTitleCase(label)}</Box>
                                        <Box variant='h1'>{value}</Box>
                                    </div>
                                ))}
                                <div>
                                    <Popover
                                        header='Root Cause Coverage Metric'
                                        content='This metric captures the % of CLIs where root cause was systematically determined'
                                    >
                                        <Box variant="awsui-key-label" color='text-status-info'>% Coverage with Root Cause</Box>
                                    </Popover>
                                    <Box variant='h1'>{rootCauseCoverage}%</Box>
                                </div>
                            </ColumnLayout>
                        ) : (
                            <Box textAlign="center" padding={{ top: "l", bottom: "l" }} color="inherit">
                                <b>No data available</b>
                                <Box variant="p" color="inherit">
                                    No Variance data available to display
                                </Box>
                            </Box>
                        )}
                    </Container>
                    <TPPieChart
                        data={varianceAnalysisData}
                        totalUnits={totalCli}
                        ariaDescription="Donut chart showing ATP reporting dashboard data."
                        ariaLabel="ATP reporting Donut chart"
                        emptyDataDescription='No variance data available to display on the chart'
                        noMatchDataDescription='No variance data available for the selected filter criteria'
                    />
                </Grid>
                )}
                {selectedReportType === REPORT_TYPE_ALL && (
                <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
                    <Container header={
                        <Header variant="h2" description="Status of all CLIs processed in ATP during the selected period.">
                            Month End Close Status
                        </Header>}>
                        {mecCliExecutionStatusDetails ? (
                            <ColumnLayout columns={4} variant="text-grid">
                                <div>
                                    <Box variant="awsui-key-label">Total Records Processed</Box>
                                    <Box variant='h1'>{totalTPRecords}</Box>
                                </div>
                                <div>
                                    <Box variant="awsui-key-label">Total CLIs Processed</Box>
                                    <Box variant='h1'>{totalCli}</Box>
                                </div>
                                <div>
                                    <Box variant="awsui-key-label">Total ICRS Records Processed</Box>
                                    <Box variant='h1'>{totalICRSRecords}</Box>
                                </div>
                                {Object.entries(mecCliExecutionStatusDetails).map(([label, value], index) => (
                                    <div key={index}>
                                        <Box variant="awsui-key-label">{convertCamelCaseToTitleCase(label)}</Box>
                                        <Box variant='h1'>{value}</Box>
                                    </div>
                                ))}
                            </ColumnLayout>
                        ) : (
                            <Box textAlign="center" padding={{ top: "l", bottom: "l" }} color="inherit">
                                <b>No data available</b>
                                <Box variant="p" color="inherit">
                                    No MEC CLI Execution Summary data available to display
                                </Box>
                            </Box>
                        )}
                    </Container>
                    <TPPieChart
                        data={atpCliStatusData}
                        totalUnits={totalCli}
                        ariaDescription="Donut chart showing ATP reporting dashboard data."
                        ariaLabel="ATP reporting Donut chart"
                        emptyDataDescription='No month end close data available to display on the chart'
                        noMatchDataDescription='No month end close data available for the selected filter criteria'
                    />
                </Grid>
                )}
                    </SpaceBetween>
                )}
            </SpaceBetween>
        </Container>
    )

};